import { useState } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { uploadImageToS3, validateImageUpload } from "@/services/s3Service";
import { ImagePlus } from "lucide-react";

export default function NewSlide() {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      // Dosya validasyonu
      const validationError = validateImageUpload([selectedFile]);
      if (validationError) {
        toast({ title: "Hata", description: validationError, variant: "destructive" });
        return;
      }
      
      setFile(selectedFile);
      setPreviewUrl(URL.createObjectURL(selectedFile));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) {
      toast({ title: "Hata", description: "Lütfen bir resim seçin.", variant: "destructive" });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Son order_num'u bul
      const { data: slides } = await supabase
        .from("home_slides")
        .select("order_num")
        .order("order_num", { ascending: false })
        .limit(1);
      const nextOrder = slides && slides.length > 0 ? (slides[0].order_num || 0) + 1 : 1;
      
      // Resmi S3'e yükle
      const storage_path = await uploadImageToS3(file, "slides");
      
      // Supabase'e ekle
      const { error } = await supabase.from("home_slides").insert({ 
        title, 
        url: "", 
        order_num: nextOrder, 
        storage_path 
      });
      
      if (error) {
        toast({ title: "Hata", description: "Slayt eklenemedi.", variant: "destructive" });
      } else {
        toast({ title: "Slayt eklendi", description: "Yeni slayt başarıyla eklendi." });
        navigate("/content/home/slides");
      }
    } catch (error) {
      console.error("Yükleme hatası:", error);
      toast({ title: "Hata", description: "Resim yüklenirken bir hata oluştu.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentLayout title="Yeni Slayt Ekle" backLink="/content/home/slides">
      <form onSubmit={handleSubmit} className="p-6 space-y-6 max-w-xl mx-auto">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Başlık</Label>
            <Input
              id="title"
              placeholder="Slayt başlığı"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label>Resim</Label>
            <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
              ${previewUrl ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary"}`}>
              <input
                type="file"
                id="slide-image"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              
              {previewUrl ? (
                <div className="relative group">
                  <img 
                    src={previewUrl} 
                    alt="Yüklenen resim önizleme" 
                    className="max-h-64 mx-auto rounded-lg" 
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      setFile(null);
                      setPreviewUrl(null);
                    }}
                  >
                    Değiştir
                  </Button>
                </div>
              ) : (
                <>
                  <label htmlFor="slide-image" className="cursor-pointer">
                    <ImagePlus className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Slayt için resim yükleyin
                    </p>
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => document.getElementById("slide-image")?.click()}
                  >
                    Resim Seç
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/content/home/slides")}>İptal</Button>
          <Button type="submit" disabled={isSubmitting}>{isSubmitting ? "Kaydediliyor..." : "Kaydet"}</Button>
        </div>
      </form>
    </ContentLayout>
  );
}
